import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import { fontWeight } from '@verantos-dev/shared-ui-components/tokens';

import type { VariableSourceRowProps } from '../types';
import { TableLinkButton } from './TableLinkButton';

export const VARIABLE_SOURCE_ROW_HEIGHT_PX = 56;
// Exported so VariableSourceListHeader's column labels line up with these cells.
export const ICON_COLUMN_WIDTH_PX = 20;
export const NUMBER_COLUMN_WIDTH_PX = 28;
export const TYPE_COLUMN_WIDTH_PX = 72;
export const DELETE_COLUMN_WIDTH_PX = 32;

export const VariableSourceRow = ({
  row,
  isEditDisabled,
  deleteTooltip,
  dragHandleProps,
}: VariableSourceRowProps) => {
  const { formatMessage } = useIntl();

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={3}
      data-testid={`analysis-variable-row-${row.key}`}
      sx={{ px: 4, minHeight: VARIABLE_SOURCE_ROW_HEIGHT_PX, bgcolor: 'background.paper' }}
    >
      {row.status === 'default' ? (
        <LockOutlinedIcon aria-hidden fontSize="small" sx={{ color: 'text.disabled' }} />
      ) : dragHandleProps ? (
        <IconButton
          size="small"
          disableRipple
          aria-label={formatMessage({
            id: 'analysisDetail.tables.preview.variableCard.reorder',
          })}
          data-testid={`analysis-variable-row-${row.key}-drag-handle`}
          sx={{
            p: 0,
            width: ICON_COLUMN_WIDTH_PX,
            borderRadius: 0,
            color: 'text.secondary',
            cursor: 'grab',
            touchAction: 'none',
          }}
          {...dragHandleProps.attributes}
          {...dragHandleProps.listeners}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      ) : (
        <DragIndicatorIcon aria-hidden fontSize="small" sx={{ color: 'text.disabled' }} />
      )}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ width: NUMBER_COLUMN_WIDTH_PX, flexShrink: 0 }}
      >
        {row.number}
      </Typography>
      <Stack spacing={1} sx={{ flexGrow: 1, minWidth: 0, py: 2 }}>
        <Typography
          variant="body2"
          title={row.title}
          sx={{
            fontWeight: fontWeight.semibold,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.onClick ? (
            <TableLinkButton
              onClick={row.onClick}
              data-testid={`analysis-variable-row-${row.key}-link`}
            >
              {row.title}
            </TableLinkButton>
          ) : (
            row.title
          )}
        </Typography>
        {row.status === 'intent' && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <AddIcon aria-hidden fontSize="inherit" sx={{ color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {formatMessage({
                id: 'analysisDetail.tables.preview.variableRow.noExistingVariable',
              })}
            </Typography>
          </Stack>
        )}
      </Stack>
      {row.status === 'default' ? (
        <Chip
          size="small"
          variant="outlined"
          label={formatMessage({ id: 'analysisDetail.tables.preview.variableCard.chip.default' })}
        />
      ) : row.isUsedInTable ? (
        <Stack direction="row" alignItems="center" spacing={2}>
          <TableChartOutlinedIcon aria-hidden fontSize="small" sx={{ color: 'text.disabled' }} />
          <Chip size="small" variant="outlined" label={row.chipLabel} />
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" noWrap>
          {row.chipLabel}
        </Typography>
      )}
      <Typography
        variant="overline"
        color="text.secondary"
        noWrap
        sx={{ width: TYPE_COLUMN_WIDTH_PX, flexShrink: 0, textAlign: 'right' }}
      >
        {row.typeLabel}
      </Typography>
      <Box sx={{ width: DELETE_COLUMN_WIDTH_PX, flexShrink: 0 }}>
        {row.onDelete && (
          <Tooltip title={deleteTooltip ?? ''} disableHoverListener={!deleteTooltip}>
            {/* span wrapper so the tooltip still shows when the button is disabled */}
            <span>
              <IconButton
                onClick={row.onDelete}
                disabled={isEditDisabled}
                size="small"
                aria-label={formatMessage({
                  id: 'analysisDetail.tables.preview.variableCard.delete',
                })}
                data-testid={`analysis-variable-row-${row.key}-delete`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Stack>
  );
};
